import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { notify } from 'messages';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';

// ---- Interfaces ----

export interface MonitoringDashboard {
  dashboard_id: number;
  name: string;
  zone_id: number;
  zone_name: string;
  item_count?: number;
  // Folder support (nap-backend #1076). Zone-scoped.
  folder_id?: number | null;
  folder_path?: FolderPath | null;
  created_at?: string;
  updated_at?: string;
}

export interface DashboardItemChildNode {
  flow_node_id: string;
  label: string;
  name: string;
  type: string;
}

export interface DashboardItem {
  dashboard_item_id?: number;
  dashboard_id?: number;
  node_definition_id: number;
  node_definition_name?: string;
  node_definition_type?: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  child_nodes?: DashboardItemChildNode[];
}

export interface FlowInstanceStatus {
  node_instance_id: number;
  status: string;
  sub_status: string | null;
  scheduled_start_datetime: string;
  start_datetime: string | null;
  end_datetime: string | null;
}

export interface ChildNodeInstanceStatus {
  node_instance_id: number;
  node_id: string;
  node_definition_name: string;
  status: string;
  sub_status: string | null;
  type: string;
}

export interface ViewerItem extends DashboardItem {
  flow_instance: FlowInstanceStatus | null;
  child_node_instances: ChildNodeInstanceStatus[];
}

export interface EventConnection {
  source_node_definition_id: number;
  target_node_definition_id: number;
  event_id: number;
  event_name: string;
}

export interface ViewerData {
  dashboard: MonitoringDashboard;
  items: ViewerItem[];
  event_connections: EventConnection[];
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface MonitoringDashboardsState {
  dashboards: MonitoringDashboard[];
  currentDashboard: (MonitoringDashboard & { items?: DashboardItem[] }) | null;
  viewerData: ViewerData | null;
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    fetchDashboards: boolean;
    fetchDashboardById: boolean;
    createDashboard: boolean;
    updateDashboard: boolean;
    deleteDashboard: boolean;
    fetchViewerData: boolean;
  };
}

// ---- Initial State ----

const initialState: MonitoringDashboardsState = {
  dashboards: [],
  currentDashboard: null,
  viewerData: null,
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 50,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchDashboards: false,
    fetchDashboardById: false,
    createDashboard: false,
    updateDashboard: false,
    deleteDashboard: false,
    fetchViewerData: false,
  },
};

// ---- Async Thunks ----

export const fetchDashboards = createAsyncThunk('monitoringDashboards/fetchDashboards', async (params: Record<string, any>) => {
  const queryParams = {
    limit: initialState.pagination.pageSize,
    ...params,
  };
  const response = await api.get('/monitoring_dashboards', {
    params: flattenQuery(queryParams),
  });
  return response.data;
});

export const fetchDashboardById = createAsyncThunk<{ dashboard: MonitoringDashboard; items: DashboardItem[] }, number>(
  'monitoringDashboards/fetchDashboardById',
  async (dashboardId) => {
    const response = await api.get(`/monitoring_dashboards/${dashboardId}`);
    return response.data;
  }
);

export const createDashboard = createAsyncThunk<
  { dashboard: MonitoringDashboard; items: DashboardItem[] },
  {
    name: string;
    zone_id?: number;
    zone_name?: string;
    folder_id: number | null;
    items?: Array<{
      node_definition_id: number;
      position_x: number;
      position_y: number;
      width: number;
      height: number;
    }>;
  },
  { rejectValue: { message: string; error?: string } }
>('monitoringDashboards/createDashboard', async (data, { rejectWithValue }) => {
  try {
    const response = await api.post('/monitoring_dashboards', data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

export const updateDashboard = createAsyncThunk<
  { dashboard: MonitoringDashboard; items: DashboardItem[] },
  {
    dashboard_id: number;
    name?: string;
    zone_id?: number;
    zone_name?: string;
    folder_id: number | null;
    items?: Array<{
      node_definition_id: number;
      position_x: number;
      position_y: number;
      width: number;
      height: number;
    }>;
  },
  { rejectValue: { message: string; error?: string } }
>('monitoringDashboards/updateDashboard', async (data, { rejectWithValue }) => {
  try {
    const response = await api.put(`/monitoring_dashboards/${data.dashboard_id}`, data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

export const deleteDashboard = createAsyncThunk<{ message: string }, number, { rejectValue: { message: string; error?: string } }>(
  'monitoringDashboards/deleteDashboard',
  async (dashboardId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/monitoring_dashboards/${dashboardId}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const fetchViewerData = createAsyncThunk<ViewerData, { dashboard_id: number; date_from: string; date_to: string }>(
  'monitoringDashboards/fetchViewerData',
  async ({ dashboard_id, date_from, date_to }) => {
    const response = await api.post(`/monitoring_dashboards/${dashboard_id}/viewer`, {
      date_from,
      date_to,
    });
    return response.data;
  }
);

// ---- Slice ----

const monitoringDashboardsSlice = createSlice({
  name: 'monitoringDashboards',
  initialState,
  reducers: {
    clearCurrentDashboard: (state) => {
      state.currentDashboard = null;
    },
    clearViewerData: (state) => {
      state.viewerData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ================== fetchDashboards ==================
      .addCase(fetchDashboards.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchDashboards = true;
      })
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { dashboards, config, filtersConfig, pagination } = action.payload;
        state.dashboards = dashboards;
        state.config = config;
        state.filtersConfig = filtersConfig || [];
        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);
        state.loading.fetchDashboards = false;
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch dashboards';
        state.loading.fetchDashboards = false;
      });

    builder
      // ================== fetchDashboardById ==================
      .addCase(fetchDashboardById.pending, (state) => {
        state.loading.fetchDashboardById = true;
        state.error = null;
      })
      .addCase(fetchDashboardById.fulfilled, (state, action) => {
        state.currentDashboard = {
          ...action.payload.dashboard,
          items: action.payload.items,
        };
        state.loading.fetchDashboardById = false;
      })
      .addCase(fetchDashboardById.rejected, (state, action) => {
        state.loading.fetchDashboardById = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      });

    builder
      // ================== createDashboard ==================
      .addCase(createDashboard.pending, (state) => {
        state.loading.createDashboard = true;
        state.error = null;
      })
      .addCase(createDashboard.fulfilled, (state, action) => {
        state.dashboards.push(action.payload.dashboard);
        state.currentDashboard = {
          ...action.payload.dashboard,
          items: action.payload.items,
        };
        state.status = 'succeeded';
        state.loading.createDashboard = false;
        const messageKey = 'data-created-dashboard';
        if (!notify.isShowing(messageKey)) {
          notify.success('Dashboard created successfully!', { key: messageKey });
        }
      })
      .addCase(createDashboard.rejected, (state, action) => {
        state.loading.createDashboard = false;
        state.status = 'failed';
        const payload = action.payload as { message?: string; error?: string } | undefined;
        state.error = payload?.error || payload?.message || 'Failed to create dashboard';
        const messageKey = 'data-failed-to-create-dashboard';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to create dashboard! ${state.error}`, {
            key: messageKey,
          });
        }
      });

    builder
      // ================== updateDashboard ==================
      .addCase(updateDashboard.pending, (state) => {
        state.loading.updateDashboard = true;
        state.error = null;
      })
      .addCase(updateDashboard.fulfilled, (state, action) => {
        const updatedDashboard = action.payload.dashboard;
        const index = state.dashboards.findIndex((d) => d.dashboard_id === updatedDashboard.dashboard_id);
        if (index !== -1) {
          state.dashboards[index] = updatedDashboard;
        }
        state.currentDashboard = {
          ...updatedDashboard,
          items: action.payload.items,
        };
        state.status = 'succeeded';
        state.loading.updateDashboard = false;
        const messageKey = 'data-updated-dashboard';
        if (!notify.isShowing(messageKey)) {
          notify.success('Dashboard saved successfully!', { key: messageKey });
        }
      })
      .addCase(updateDashboard.rejected, (state, action) => {
        state.loading.updateDashboard = false;
        state.status = 'failed';
        const payload = action.payload as { message?: string; error?: string } | undefined;
        state.error = payload?.error || payload?.message || 'Failed to update dashboard';
        const messageKey = 'data-failed-to-update-dashboard';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to save dashboard! ${state.error}`, { key: messageKey });
        }
      });

    builder
      // ================== deleteDashboard ==================
      .addCase(deleteDashboard.pending, (state) => {
        state.loading.deleteDashboard = true;
        state.error = null;
      })
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        state.dashboards = state.dashboards.filter((d) => d.dashboard_id !== action.meta.arg);
        state.status = 'succeeded';
        state.loading.deleteDashboard = false;
        const messageKey = 'data-deleted-dashboard';
        if (!notify.isShowing(messageKey)) {
          notify.success('Dashboard deleted successfully!', { key: messageKey });
        }
      })
      .addCase(deleteDashboard.rejected, (state, action) => {
        state.loading.deleteDashboard = false;
        state.status = 'failed';
        const payload = action.payload as { message?: string; error?: string } | undefined;
        state.error = payload?.error || payload?.message || 'Failed to delete dashboard';
        const messageKey = 'data-failed-to-delete-dashboard';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete dashboard! ${state.error}`, {
            key: messageKey,
          });
        }
      });

    builder
      // ================== fetchViewerData ==================
      .addCase(fetchViewerData.pending, (state) => {
        state.loading.fetchViewerData = true;
        state.error = null;
        // Do NOT clear viewerData during refresh to avoid flickering
      })
      .addCase(fetchViewerData.fulfilled, (state, action) => {
        state.viewerData = action.payload;
        state.loading.fetchViewerData = false;
      })
      .addCase(fetchViewerData.rejected, (state, action) => {
        state.loading.fetchViewerData = false;
        state.error = action.error.message || 'Failed to fetch viewer data';
        const messageKey = 'data-failed-to-fetch-viewer';
        if (!notify.isShowing(messageKey)) {
          notify.error('Failed to load dashboard data. Please try again later.', { key: messageKey });
        }
      });
  },
});

export const { clearCurrentDashboard, clearViewerData } = monitoringDashboardsSlice.actions;
export default monitoringDashboardsSlice.reducer;
